import {
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import { Monitor, Moon, Sun, type LucideIcon } from "lucide-react";
import { useAuth } from "@/auth/AuthProvider";
import { useThemeChoice } from "@/theme/BrkThemeProvider";
import { THEME_CHOICES, type ThemeChoice } from "@/theme/themeChoice";

// The top-bar user menu (BRK-S035, reworked by BRK-S242). The trigger carries
// the broker's initials and first name; the dropdown opens on who is signed in
// (name, email, brokerage), then the broker's own destinations (Profile,
// Account security, Notification preferences) and Sign out. It is the shell's
// one place for the broker's identity: the sidebar no longer repeats it. The
// Theme control (Day / Night / System, BRK-S245) sits under the identity.
// Accessible: aria-haspopup + aria-expanded (+ aria-controls while open) on the
// trigger; the panel closes on Escape, an outside click, and on selecting an
// item.
interface UserMenuProps {
  /** From the profile read; null while it loads, or if it fails. */
  firstName: string | null;
  fullName: string | null;
  email: string;
  /** Avatar initials: the name's, else the email's. */
  initials: string;
  /** Brokerage name (a neutral fallback while it loads). */
  brokerageName: string;
}

const THEME_OPTIONS: Record<ThemeChoice, { label: string; Icon: LucideIcon }> =
  {
    day: { label: "Day", Icon: Sun },
    night: { label: "Night", Icon: Moon },
    system: { label: "System", Icon: Monitor },
  };

/** Day / Night / System as a radio group: one tab stop (the checked choice),
 *  and the arrow keys move and choose, wrapping round. A choice applies at
 *  once and is remembered on this device (BrkThemeProvider). */
function ThemeControl() {
  const { choice, setChoice } = useThemeChoice();
  const labelId = useId();
  const radios = useRef<Partial<Record<ThemeChoice, HTMLButtonElement | null>>>(
    {},
  );

  function onKeyDown(event: ReactKeyboardEvent) {
    const step =
      event.key === "ArrowRight" || event.key === "ArrowDown"
        ? 1
        : event.key === "ArrowLeft" || event.key === "ArrowUp"
          ? -1
          : 0;
    if (step === 0) return;
    event.preventDefault();
    const count = THEME_CHOICES.length;
    const next =
      THEME_CHOICES[(THEME_CHOICES.indexOf(choice) + step + count) % count];
    setChoice(next);
    radios.current[next]?.focus();
  }

  return (
    <div className="border-b border-brk-border px-4 py-3">
      <p
        id={labelId}
        className="text-xs font-semibold uppercase tracking-wide text-brk-dim"
      >
        Theme
      </p>
      <div
        role="radiogroup"
        aria-labelledby={labelId}
        onKeyDown={onKeyDown}
        className="mt-2 grid grid-cols-3 gap-1 rounded-md bg-brk-bg p-1"
      >
        {THEME_CHOICES.map((option) => {
          const { label, Icon } = THEME_OPTIONS[option];
          const checked = option === choice;
          return (
            <button
              key={option}
              ref={(el) => {
                radios.current[option] = el;
              }}
              type="button"
              role="radio"
              aria-checked={checked}
              tabIndex={checked ? 0 : -1}
              onClick={() => setChoice(option)}
              className={`flex items-center justify-center gap-1.5 rounded px-2 py-1.5 text-xs font-medium transition-colors ${
                checked
                  ? "bg-brk-panel text-brk-accent shadow-sm"
                  : "text-brk-dim hover:text-brk-text"
              }`}
            >
              <Icon size={14} aria-hidden />
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function UserMenu({
  firstName,
  fullName,
  email,
  initials,
  brokerageName,
}: UserMenuProps) {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const panelId = useId();

  // Close on Escape or a click outside the menu, but only while it's open.
  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  async function onSignOut() {
    setSigningOut(true);
    await signOut();
    navigate("/", { replace: true });
  }

  const itemClass =
    "block w-full px-4 py-2 text-left text-sm text-brk-text transition-colors hover:bg-brk-bg";

  return (
    <div ref={containerRef} className="relative">
      {/* The visible first name is part of the accessible name (WCAG 2.5.3);
          "Account" stands in while the profile loads, or if it can't. Below
          sm the trigger is the initials alone, so the header fits a phone
          (BRK-S248); the name stays in the accessible name. */}
      <button
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-label={firstName ? `Account menu, ${firstName}` : "Account menu"}
        onClick={() => setOpen((prev) => !prev)}
        className="flex h-8 items-center justify-center gap-2 rounded-full p-0.5 text-sm font-medium text-brk-text transition-colors hover:bg-brk-bg sm:pr-2 [@media(pointer:coarse)]:h-11 [@media(pointer:coarse)]:min-w-11"
      >
        <span
          aria-hidden="true"
          className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-brk-border bg-brk-bg text-xs font-semibold"
        >
          {initials}
        </span>
        <span className="hidden max-w-[5.5rem] truncate sm:block sm:max-w-[9rem]">
          {firstName ?? "Account"}
        </span>
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
          className={`hidden shrink-0 text-brk-dim transition-transform sm:block ${open ? "rotate-180" : ""}`}
        >
          <path
            d="m6 9 6 6 6-6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open && (
        <div
          id={panelId}
          className="absolute right-0 z-40 mt-2 w-64 overflow-hidden rounded-md border border-brk-border bg-brk-panel shadow-lg"
        >
          {/* Who is signed in. */}
          <div className="border-b border-brk-border px-4 py-3 leading-tight">
            {fullName && (
              <p className="truncate text-sm font-semibold text-brk-text">
                {fullName}
              </p>
            )}
            <p className="mt-0.5 truncate text-sm text-brk-dim" title={email}>
              {email}
            </p>
            <p className="mt-1 truncate text-xs text-brk-dim">
              {brokerageName}
            </p>
          </div>
          <ThemeControl />
          <div className="py-1">
            <Link
              to="/profile"
              onClick={() => setOpen(false)}
              className={itemClass}
            >
              Profile
            </Link>
            <Link
              to="/security"
              onClick={() => setOpen(false)}
              className={itemClass}
            >
              Account security
            </Link>
            <Link
              to="/preferences"
              onClick={() => setOpen(false)}
              className={itemClass}
            >
              Notification preferences
            </Link>
          </div>
          <div className="border-t border-brk-border py-1">
            <button
              type="button"
              onClick={onSignOut}
              disabled={signingOut}
              className={`${itemClass} text-brk-danger disabled:opacity-60`}
            >
              {signingOut ? "Signing out…" : "Sign out"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
